import { Controller } from "@hotwired/stimulus"

// The foot of a page is not a door to be opened. A reader who comes to the end
// of a feed has the next page brought to them and goes on reading; the "Older
// posts" link stays under it for a tap, and is the whole of it where there is
// no JavaScript. Posts are only ever added below - nothing moves under
// somebody who is reading - and the page ends where the archive does.
//
// The list it reads into is the section of plates above it (id="posts"), and
// the next page is the link's own href, so every feed that offers an older
// page is carried on the same way.
export default class extends Controller {
  static targets = ["link"]

  // How near the foot the reader must come for the next page to be fetched,
  // and how many pages may arrive before they scroll again. A page thinned of
  // its readings can hold few posts, so a few may follow one another; not the
  // whole archive at once.
  static REACH = 800
  static BURST = 4

  connect() {
    this.list = document.getElementById("posts")
    if (!this.list) return

    this.rounds = 0
    this.mark = window.scrollY
    this.look = () => this.maybe()
    window.addEventListener("scroll", this.look, { passive: true })
    window.addEventListener("resize", this.look)
    this.maybe()
  }

  disconnect() {
    window.removeEventListener("scroll", this.look)
    window.removeEventListener("resize", this.look)
  }

  // Within reach of the foot, and not already fetching. Moving the page at all
  // says the reader is still reading, and the pages taken unasked begin again.
  maybe() {
    if (Math.abs(window.scrollY - this.mark) > 300) {
      this.mark = window.scrollY
      this.rounds = 0
    }
    if (this.busy || this.rounds >= this.constructor.BURST) return
    if (this.element.getBoundingClientRect().top > window.innerHeight + this.constructor.REACH) return

    this.rounds += 1
    this.onward()
  }

  // The next page, put at the foot of the list. Also what the link does, so a
  // tap and a scroll come to the same thing.
  async onward(event) {
    event?.preventDefault()
    if (this.busy || !this.hasLinkTarget) return

    this.busy = true
    this.element.setAttribute("aria-busy", "true")
    try {
      const response = await fetch(this.linkTarget.href, { headers: { Accept: "text/html" } })
      if (response.ok) this.read(await response.text())
    } catch { /* offline, perhaps; the link is still there to be tapped */ }
    this.busy = false
    this.element.removeAttribute("aria-busy")
  }

  // What the fetched page held: its posts, and where the page after that is.
  // Nothing after the last page, so the link goes with it.
  read(html) {
    const page = new DOMParser().parseFromString(html, "text/html")
    const have = new Set([...this.list.querySelectorAll("[data-slug]")].map((el) => el.dataset.slug))
    const posts = [...(page.getElementById("posts")?.querySelectorAll(":scope > article[data-slug]") || [])]
      .filter((post) => !have.has(post.dataset.slug))

    posts.forEach((post) => this.list.append(post))

    const next = page.querySelector("[data-controller~='onward'] a[rel='next']")
    if (!next) return this.element.remove()

    this.linkTarget.href = next.getAttribute("href")
    if (posts.length > 0) this.maybe()
    return true
  }
}
